import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import ProgressBar from '@/components/ProgressBar';
import ReturnToDashboardPill from '@/components/ReturnToDashboardPill';
import PreparingScreen from '@/components/PreparingScreen';
import useDeepLinkParams from '@/hooks/useDeepLinkParams';
import useTaskCompletion from '@/hooks/useTaskCompletion';
import useAutoGenerate from '@/hooks/useAutoGenerate';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { botRunsAPI, projectsAPI } from '@/utils/api';
import { toast } from 'sonner';
import { FloppyDisk } from '@phosphor-icons/react';
import MissionFinalizeActions from '@/components/MissionFinalizeActions';

const MoneyBotPage = () => {
  const navigate = useNavigate();
  const deepLink = useDeepLinkParams();
  const { completeIfDeepLinked } = useTaskCompletion();
  const flags = useFeatureFlags();
  const [step, setStep] = useState('input');
  const [status, setStatus] = useState('pending');
  const [runId, setRunId] = useState(null);
  const [formData, setFormData] = useState({
    niche: '',
    monetization_type: 'digital products',
    audience_type: 'beginners',
    current_stage: 'just starting',
  });
  const [outputs, setOutputs] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStep('running');
    setStatus('running');

    try {
      const response = await botRunsAPI.create({ bot_type: 'money', inputs: formData });
      setRunId(response.data.id);
      setOutputs(response.data.outputs);
      setStatus('completed');
      setTimeout(() => setStep('result'), 1000);
      toast.success('Monetization plan generated!');
    } catch (error) {
      setStatus('failed');
      toast.error('Failed to generate monetization plan.');
    }
  };

  const handleSave = async () => {
    const projectName = prompt('Enter a name for this project:');
    if (!projectName) return;
    setSaving(true);
    try {
      const res = await projectsAPI.create({ project_name: projectName, bot_run_id: runId });
      toast.success('Saved to Projects!');
      // Round 96 — close the Round-94 workflow loop.
      await completeIfDeepLinked(
        { asset_id: res?.data?.id || runId, saved_at: new Date().toISOString() },
      );
    } catch (error) {
      toast.error('Failed to save project.');
    } finally {
      setSaving(false);
    }
  };

  // Round 95 — deep-link acceptance from <TaskActionButton />.
  useEffect(() => {
    if (deepLink.hasDeepLink) {
      console.info('[MoneyBot] deep-link accepted', deepLink);
    }
  }, [deepLink.hasDeepLink, deepLink.asset_id, deepLink.task_id]);

  // Post-Round-96 execution loop — invoke the money-bot generator
  // on arrival using existing business memory. Sub-asset (offer /
  // sales_page / email / lead_magnet) is taken from the deep-link
  // `asset_type` param.
  //
  // Round 101 — prefer the exact mission context threaded via
  // `deepLink.mission` (from the Generate chip's `wctx` blob)
  // over generic Business Memory. This is how the builder
  // continues the exact mission the customer just approved.
  const buildMoneyInputs = useCallback((memory) => {
    const m = deepLink.mission || {};
    const approved = m.approved_edits || {};
    const summary = m.asset_payload_summary || {};
    return {
      niche: m.niche || memory.niche || memory.business_name || formData.niche || '',
      business_type: memory.business_type || formData.business_type || 'digital_product',
      audience: m.target_audience || memory.target_audience || formData.audience || '',
      asset_focus: deepLink.asset_type || m.asset_type || 'offer',
      mission_context: (m && Object.keys(m).length) ? {
        asset_id: m.asset_id,
        asset_type: m.asset_type,
        mission_id: m.mission_id,
        mission_title: m.mission_title,
        // Round 104 — approved edits win.
        approved_hook: approved.hook || summary.hook,
        approved_caption: approved.caption || summary.caption,
        approved_cta: approved.cta || approved.cta_copy || summary.cta,
        approved_body: approved.body || approved.full_message,
        approved_subject: approved.subject,
        approved_headline: approved.headline,
        approved_pitch: approved.pitch,
        approved_price: approved.price,
        publishing_instructions: approved.publishing_instructions
          || m.publishing_instructions,
        preferred_platform: approved.platform
          || approved.recommended_platform
          || m.preferred_platform,
      } : undefined,
      business_context: {
        business_name: m.business_name || memory.business_name || '',
        primary_offer: memory.primary_offer || '',
        pricing: memory.pricing || '',
        target_audience: m.target_audience || memory.target_audience || '',
      },
    };
  }, [
    formData.niche, formData.business_type, formData.audience,
    deepLink.asset_type, deepLink.mission,
  ]);

  const handleAutoResult = useCallback((resp) => {
    setRunId(resp?.data?.id);
    setOutputs(resp?.data?.outputs);
    setStatus('completed');
    setStep('result');
    const what = ({
      offer: 'today\u2019s offer',
      sales_page: 'today\u2019s sales page',
      email_sequence: 'today\u2019s email',
      lead_magnet: 'today\u2019s lead magnet',
    })[deepLink.asset_type] || 'today\u2019s monetization plan';
    toast.success(
      `Great work! ${what.charAt(0).toUpperCase() + what.slice(1)} is ready.`,
      { description: 'Review, refine, and approve when you\u2019re happy.' },
    );
  }, [deepLink.asset_type]);

  const auto = useAutoGenerate({
    botType: 'money',
    buildInputs: buildMoneyInputs,
    onResult: handleAutoResult,
    enabled: Boolean(deepLink.mission) || Boolean(flags.execution_loop_v1),
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-4 flex items-center justify-between">
          <ReturnToDashboardPill testId="money-bot-return-pill" />
          {deepLink.hasDeepLink && (
            <span
              className="text-xs text-slate-500"
              data-testid="money-bot-deep-link-indicator"
              data-workflow-task-id={deepLink.task_id || ''}
              data-workflow-asset-id={deepLink.asset_id || ''}
              data-workflow-mode={deepLink.mode || ''}
              data-workflow-asset-type={deepLink.asset_type || ''}
            >
              Deep-linked from your Dashboard task
            </span>
          )}
        </div>
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" data-testid="money-bot-title">Money Bot</h1>
          <p className="text-slate-600">Generate monetization strategies and offer ideas</p>
        </div>

        {/* Round 102 — Mission continuation panel. Shown whenever
             the customer arrived via a Today's Mission Generate
             chip (`deepLink.mission` present) so they never see
             the generic Money Bot form as a fallback. */}
        {deepLink.mission && (step === 'input' || auto.isPreparing) && !outputs && (
          <div
            className="mb-6 rounded-xl border border-amber-200 bg-white shadow-sm p-6"
            data-testid="money-bot-mission-continuation"
          >
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-600 mb-1">
                  Continuing your mission
                </p>
                <h2
                  className="text-xl font-semibold text-slate-900 truncate"
                  data-testid="money-bot-mission-title"
                >
                  {deepLink.mission.mission_title || 'Today\u2019s mission'}
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  {deepLink.mission.business_name || ''}
                  {deepLink.mission.niche ? ` \u00b7 ${deepLink.mission.niche}` : ''}
                  {deepLink.mission.preferred_platform
                    ? ` \u00b7 ${deepLink.mission.preferred_platform}` : ''}
                </p>
              </div>
              <span className="shrink-0 inline-flex items-center px-2 py-1 rounded-md bg-amber-100 text-amber-700 text-[10px] font-semibold uppercase tracking-wider">
                {deepLink.mission.asset_type || 'money'}
              </span>
            </div>
            {auto.isPreparing && (
              <p className="mt-4 text-xs text-slate-500 flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
                Generating from your approved mission&hellip;
              </p>
            )}
          </div>
        )}

        {auto.isPreparing && !deepLink.mission && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <PreparingScreen
              assetType={auto.assetKind || 'offer'}
              testId="money-bot-preparing"
            />
          </div>
        )}

        {!auto.isPreparing && step === 'input' && !deepLink.mission && (
          <form onSubmit={handleSubmit} data-testid="money-bot-form" className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Your Niche *</label>
              <input type="text" name="niche" required data-testid="money-niche-input" value={formData.niche} onChange={handleChange} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500" placeholder="e.g., fitness, marketing" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Monetization Type *</label>
              <select name="monetization_type" data-testid="money-type-select" value={formData.monetization_type} onChange={handleChange} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500">
                <option value="digital products">Digital Products</option>
                <option value="affiliate">Affiliate Marketing</option>
                <option value="coaching">Coaching</option>
                <option value="services">Services</option>
                <option value="lead generation">Lead Generation</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Audience Type *</label>
              <select name="audience_type" data-testid="money-audience-select" value={formData.audience_type} onChange={handleChange} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500">
                <option value="beginners">Beginners</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Current Stage *</label>
              <select name="current_stage" data-testid="money-stage-select" value={formData.current_stage} onChange={handleChange} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500">
                <option value="just starting">Just Starting</option>
                <option value="small audience">Small Audience</option>
                <option value="active audience">Active Audience</option>
              </select>
            </div>
            <button type="submit" data-testid="money-bot-submit" className="w-full py-4 px-6 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg">Generate Monetization Plan</button>
          </form>
        )}

        {step === 'running' && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-6" data-testid="money-bot-running">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
              </div>
              <h3 className="text-xl font-semibold text-slate-900">Creating Your Monetization Strategy...</h3>
            </div>
            <ProgressBar status={status} />
          </div>
        )}

        {step === 'result' && outputs && (
          <div className="space-y-6" data-testid="money-bot-results">
            {/* Round 104 — Approve & Generate finalize + return bar */}
            <MissionFinalizeActions
              outputs={outputs}
              botRunId={runId}
              projectName={(deepLink.mission && deepLink.mission.mission_title) || 'Money Mission'}
              buildMetaPatch={(o) => {
                const focus = deepLink.asset_type
                  || (deepLink.mission && deepLink.mission.asset_type)
                  || 'offer';
                const firstIdea = Array.isArray(o?.offer_ideas) && o.offer_ideas.length
                  ? o.offer_ideas[0] : undefined;
                const firstCta = Array.isArray(o?.cta_suggestions) && o.cta_suggestions.length
                  ? o.cta_suggestions[0] : undefined;
                const patch = { cta: firstCta };
                if (focus === 'lead_magnet') {
                  patch.body = o?.strategy || undefined;
                  patch.suggested_social_post = firstIdea;
                  patch.cta_copy = firstCta;
                } else if (focus === 'email_sequence') {
                  patch.full_message = o?.strategy || undefined;
                  patch.subject = firstIdea;
                } else if (focus === 'sales_page') {
                  patch.body = o?.strategy || undefined;
                  patch.headline = firstIdea;
                } else {
                  patch.pitch = o?.strategy || firstIdea || undefined;
                }
                return patch;
              }}
              renderAsText={(o) => {
                const parts = [];
                if (o?.strategy) parts.push('STRATEGY\n' + o.strategy);
                if (Array.isArray(o?.offer_ideas) && o.offer_ideas.length) {
                  parts.push('OFFER IDEAS\n' + o.offer_ideas.map((x, i) => `${i + 1}. ${x}`).join('\n'));
                }
                if (Array.isArray(o?.cta_suggestions) && o.cta_suggestions.length) {
                  parts.push('CTA SUGGESTIONS\n' + o.cta_suggestions.join('\n'));
                }
                if (Array.isArray(o?.action_plan) && o.action_plan.length) {
                  const lines = o.action_plan.map((item, i) => {
                    if (typeof item === 'string') return `Day ${i + 1}: ${item}`;
                    return `Day ${item.day || i + 1}: ${item.action || ''}`;
                  });
                  parts.push('7-DAY ACTION PLAN\n' + lines.join('\n'));
                }
                return parts.join('\n\n');
              }}
              filenamePrefix="paygbot-money"
              testIdPrefix="money-bot-mission-actions"
            />

            <div className="flex flex-wrap gap-4">
              {!deepLink.hasDeepLink && (
                <button onClick={handleSave} disabled={saving} data-testid="save-project-button" className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg">
                  <FloppyDisk size={20} weight="duotone" />
                  <span>{saving ? 'Saving...' : 'Save to Projects'}</span>
                </button>
              )}
              <button onClick={() => { setStep('input'); setStatus('pending'); setOutputs(null); }} data-testid="run-another-button" className="px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-medium border border-slate-200 rounded-lg">Run Another</button>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">Monetization Strategy</h3>
              <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{outputs.strategy}</pre>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">Offer Ideas</h3>
              <ul className="space-y-2">
                {outputs.offer_ideas.map((idea, i) => (
                  <li key={i} className="p-3 bg-amber-50 rounded-lg text-amber-900">{idea}</li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">CTA Suggestions</h3>
              <div className="flex flex-wrap gap-2">
                {outputs.cta_suggestions.map((cta, i) => (
                  <span key={i} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg text-sm">{cta}</span>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">7-Day Action Plan</h3>
              <ul className="space-y-3">
                {outputs.action_plan.map((item, i) => (
                  <li key={i} className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    {typeof item === 'object' ? (
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                            {item.day || i + 1}
                          </span>
                          <span className="font-semibold text-amber-800">{item.action}</span>
                        </div>
                        {item.tasks && item.tasks.length > 0 && (
                          <ul className="ml-11 space-y-1">
                            {item.tasks.map((task, j) => (
                              <li key={j} className="text-slate-700 text-sm flex items-start gap-2">
                                <span className="text-amber-500 mt-1">•</span>
                                <span>{task}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-start space-x-2">
                        <span className="text-amber-600 mt-1">•</span>
                        <span className="text-slate-700">{item}</span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MoneyBotPage;